// src/components/blog/features/ErrorBoundaries/BlogErrorBoundary.tsx

'use client'

import { Component, type ReactNode, type ErrorInfo } from 'react'
import Link from 'next/link'

import { handleClientError } from '~/lib/client-error-handling'
import { clientLogger } from '~/lib/logger-client'

/**
 * Props for the BlogErrorBoundary component.
 * 
 * @interface Props
 * @property {ReactNode} children - The components to be rendered and monitored for errors.
 * @property {ReactNode | ((error: Error, resetErrorBoundary: () => void) => ReactNode)} [fallback] - Component or function to render when an error occurs.
 *   If a function is provided, it will receive the error and reset function as arguments.
 * @property {string} [componentContext] - Optional context information about where this error boundary is used.
 * @property {boolean} [captureComponentErrors=true] - Whether this error boundary should capture errors from components 
 *   that already have their own error boundaries. Setting to false helps prevent duplicate error handling.
 * @property {() => void} [onReset] - Optional callback to run when the error boundary is reset.
 */
interface Props {
  children: ReactNode
  fallback?: ReactNode | ((error: Error, resetErrorBoundary: () => void) => ReactNode)
  componentContext?: string
  captureComponentErrors?: boolean
  onReset?: () => void
}

/**
 * State for the BlogErrorBoundary component.
 * 
 * @interface State
 * @property {boolean} hasError - Indicates whether an error has been caught.
 * @property {Error | null} error - The error that was caught, if any.
 */
interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Default fallback UI to display when a blog content error occurs and no custom fallback is provided.
 * 
 * @param {Object} props - Component props.
 *   @param {Error} props.error - The error that was caught.
 *   @param {boolean} [props.componentError=false] - Whether the error occurred in a component with its own error boundary.
 *   @param {string} [props.componentContext=''] - Context information about where the error occurred.
 *   @param {() => void} [props.resetErrorBoundary] - Function to reset the error boundary state.
 * @returns {ReactNode} A styled error message UI with retry and navigation options.
 */
function DefaultErrorFallback({
  componentError = false,
  componentContext = '',
  resetErrorBoundary
}: {
  componentError?: boolean
  componentContext?: string
  resetErrorBoundary?: () => void
}): ReactNode {
  // NOTE: logging happens in `componentDidCatch` (once per caught error), not
  // here — this is a render function and would re-log on every re-render. The
  // fallback deliberately shows a generic message and never the raw error.

  return (
    <div className="relative overflow-hidden rounded-2xl border border-red-200/60 bg-white p-8 dark:border-red-800/60 dark:bg-gray-900">
      <div className="absolute inset-0 bg-grid-gray-900/[0.04] [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-white/[0.04]" />
      <div className="relative flex flex-col items-center text-center">
        <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">
          Error Loading Blog Content
          {componentContext ? ` (${componentContext})` : ''}
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-md">
          We encountered an error while loading this content. Please try again later or contact support if the issue persists.
        </p>
        <div className="mt-6 flex items-center gap-4">
          {resetErrorBoundary && (
            <button
              onClick={resetErrorBoundary}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
            >
              Try Again
            </button>
          )}
          {!resetErrorBoundary && (
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
            >
              Reload Page
            </button>
          )}
          <Link
            href="/blog"
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          >
            Return to Blog
          </Link>
          {componentError && (
            <button
              onClick={resetErrorBoundary}
              className="mt-4 px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-800 rounded-md border border-red-200 transition-colors dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/50"
            >
              Reset Component
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Renders the appropriate fallback UI based on the provided fallback prop.
 * 
 * @param {Object} props - Component props.
 *   @param {Error} props.error - The error that was caught.
 *   @param {boolean} [props.componentError=false] - Whether the error occurred in a component with its own error boundary.
 *   @param {string} [props.componentContext=''] - Context information about where the error occurred.
 *   @param {() => void} props.resetErrorBoundary - Function to reset the error boundary state.
 *   @param {ReactNode | ((error: Error, resetErrorBoundary: () => void) => ReactNode)} [props.fallback] - Custom fallback UI or function.
 * @returns {ReactNode} The fallback UI to render.
 */
function ErrorFallback({ 
  error, 
  componentError = false,
  componentContext = '',
  resetErrorBoundary,
  fallback 
}: { 
  error: Error
  componentError?: boolean
  componentContext?: string
  resetErrorBoundary: () => void
  fallback?: ReactNode | ((error: Error, resetErrorBoundary: () => void) => ReactNode) 
}): ReactNode {
  // If fallback is a function, call it with the error and reset function
  if (typeof fallback === 'function') {
    return fallback(error, resetErrorBoundary)
  }
  
  // If fallback is provided as a ReactNode, return it
  if (fallback) {
    return fallback as ReactNode
  }
  
  // Otherwise return the default fallback
  return (
    <DefaultErrorFallback
      componentError={componentError}
      componentContext={componentContext}
      resetErrorBoundary={resetErrorBoundary}
    />
  )
}

/**
 * A specialized error boundary component for blog content that catches JavaScript errors,
 * logs those errors, and displays a user-friendly fallback UI.
 * 
 * This component extends the standard ErrorBoundary pattern with blog-specific UI
 * and the ability to selectively handle errors from nested error boundaries.
 * 
 * @property {constructor(props: Props)} - Initialize the component with default state.
 * @property {static getDerivedStateFromError(error: Error): State} - Update state when an error occurs during rendering.
 * @property {componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void} - Called after an error has been thrown by a descendant component.
 * @property {resetErrorBoundary(): void} - Reset the error boundary state.
 * @property {render(): ReactNode} - Render either the children or the fallback UI based on whether an error occurred.
 * 
 * @example
 * // Basic usage with default fallback UI
 * <BlogErrorBoundary>
 *   <BlogPost />
 * </BlogErrorBoundary>
 * 
 * @example
 * // With custom fallback component
 * <BlogErrorBoundary fallback={<CustomBlogErrorUI />}>
 *   <BlogPost />
 * </BlogErrorBoundary>
 * 
 * @example
 * // With custom fallback function
 * <BlogErrorBoundary 
 *   fallback={(error, reset) => (
 *     <CustomErrorUI error={error} onReset={reset} />
 *   )}
 * >
 *   <BlogPost />
 * </BlogErrorBoundary>
 * 
 * @example
 * // With component context for better error reporting
 * <BlogErrorBoundary componentContext="Featured Posts Section">
 *   <FeaturedPosts />
 * </BlogErrorBoundary>
 * 
 * @example
 * // With custom reset handler
 * <BlogErrorBoundary onReset={() => fetchBlogPostsAgain()}>
 *   <BlogPosts />
 * </BlogErrorBoundary>
 * 
 * @example
 * // Ignoring errors from components with their own error boundaries
 * <BlogErrorBoundary captureComponentErrors={false}>
 *   <BlogPost />
 * </BlogErrorBoundary>
 */
export class BlogErrorBoundary extends Component<Props, State> {
  /**
   * Initialize the component with default state.
   * 
   * @param {Props} props - Component props.
   */
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this)
  }

  /**
   * Update state when an error occurs during rendering.
   * This is called during the "render" phase, so side-effects are not permitted.
   * 
   * @param {Error} error - The error that was thrown.
   * @returns {State} New state to be applied.
   */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  /**
   * Called after an error has been thrown by a descendant component.
   * Handles error logging and determines whether to capture or pass through errors
   * based on the captureComponentErrors prop.
   * 
   * @param {Error} error - The error that was thrown.
   * @param {ErrorInfo} errorInfo - Information about the component stack.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Throttle error logging to prevent floods. `handleClientError` classifies,
    // logs to the drain (structured `client_error` with stack + componentStack),
    // and dedupes identical faults within a 2s window; this cap is a coarser
    // flood guard on top of that for a page throwing many distinct errors.
    //
    // The cap is WINDOWED (a fixed/tumbling window), not a permanent latch: the
    // previous static counter never reset, so after MAX_PER_WINDOW
    // session-cumulative errors it suppressed ALL further error logging for the
    // rest of the session. Here the window rolls over once WINDOW_MS has elapsed
    // since it opened — up to MAX_PER_WINDOW logs per window — so a page that
    // keeps throwing recovers telemetry each new window instead of latching off.
    // (Deliberately NOT a "reset only after WINDOW_MS of quiet" cooldown: that
    // would re-latch permanently on a page that never goes quiet — the exact bug
    // this fixes. A coarse per-window cap that always recovers is the goal.)
    const now = Date.now()
    if (now - BlogErrorBoundary.windowStart > BlogErrorBoundary.WINDOW_MS) {
      BlogErrorBoundary.windowStart = now
      BlogErrorBoundary.errorCount = 0
    }

    if (BlogErrorBoundary.errorCount < BlogErrorBoundary.MAX_PER_WINDOW) {
      handleClientError(
        error,
        {
          type: 'error_boundary',
          component: 'BlogErrorBoundary',
          componentStack: errorInfo.componentStack,
          context: this.props.componentContext ?? 'unknown',
        },
        // Log-only (no reload): preserves the pre-PR inline-fallback behavior.
        { recover: false },
      )

      BlogErrorBoundary.errorCount++
    } else if (BlogErrorBoundary.errorCount === BlogErrorBoundary.MAX_PER_WINDOW) {
      clientLogger.warn('Too many errors caught by BlogErrorBoundary; suppressing further error logs this window', {
        component: 'BlogErrorBoundary',
        context: this.props.componentContext ?? 'unknown',
      })
      BlogErrorBoundary.errorCount++
    }

    // If this is a component-level error and we're not supposed to capture those,
    // reset state to let the error bubble up to a parent boundary
    const componentStack = errorInfo.componentStack ?? ''
    const isComponentError = componentStack.includes('createSafeComponent') ||
                            componentStack.includes('ErrorBoundary')
    
    if (isComponentError && this.props.captureComponentErrors === false) {
      // Reset state to allow rendering to continue - the component-level error boundary will handle it
      this.setState({ hasError: false, error: null })
    }
    
    // Here you could send the error to an error reporting service
    // Example: errorReportingService.captureError(error, { 
    //   extra: errorInfo, 
    //   tags: { 
    //     source: 'blog',
    //     context: this.props.componentContext || 'unknown'
    //   } 
    // })
  }

  /**
   * Reset the error boundary state and call the onReset callback if provided.
   * This allows the component to recover from errors and try rendering again.
   */
  resetErrorBoundary(): void {
    if (this.props.onReset) {
      this.props.onReset()
    }
    this.setState({ hasError: false, error: null })
  }

  /**
   * Render either the children or the fallback UI based on whether an error occurred.
   * 
   * @returns {ReactNode} The rendered output.
   */
  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallback, componentContext, captureComponentErrors = true } = this.props

    if (hasError && error) {
      // Create a local function that calls the bound method to avoid the unbound-method error
      const handleReset = (): void => {
        this.resetErrorBoundary()
      }
      
      return (
        <ErrorFallback 
          error={error} 
          componentError={!!captureComponentErrors}
          componentContext={componentContext}
          resetErrorBoundary={handleReset}
          fallback={fallback} 
        />
      )
    }

    return children
  }

  // Flood guard for error logging (see componentDidCatch). A fixed/tumbling
  // window, NOT a permanent latch: the count resets at the start of each new
  // WINDOW_MS window, so a page can't go permanently dark on error telemetry
  // after MAX_PER_WINDOW session-cumulative faults (the old static counter never
  // reset). Up to MAX_PER_WINDOW logs per window.
  static readonly MAX_PER_WINDOW = 5
  static readonly WINDOW_MS = 60_000
  static errorCount = 0
  static windowStart = 0
}